"use client";

import { Controller, useForm } from "react-hook-form";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";

const formSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters").max(100),
  email: z.string().email("Please enter a valid email"),
  message: z
    .string()
    .min(10, "Message must be at least 10 characters")
    .max(500),
});

type FormData = z.infer<typeof formSchema>;

export default function ContactForm() {
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { name: "", email: "", message: "" },
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = form;

  const onSubmit = async (data: FormData) => {
    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error("Failed to send message");
      toast.success("Message sent successfully!", {
        description: "We'll get back to you within 24 hours.",
        duration: 3000,
      });
      reset();
    } catch (error) {
      console.error("Form Submission Error", error);
      toast.error("Failed to send message", {
        description: "Please try again later or contact us directly.",
        duration: 3000,
      });
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto border border-border bg-card">
      <div className="grid grid-cols-1 md:grid-cols-5">
        {/* Left panel */}
        <div className="md:col-span-2 bg-primary p-10 flex flex-col justify-between">
          <div>
            <div className="font-heading text-xs font-extrabold text-primary-foreground/60 uppercase tracking-widest mb-6">
              Reach Out
            </div>
            <h2 className="font-heading text-4xl md:text-5xl font-extrabold text-primary-foreground uppercase leading-tight">
              Get In
              <br />
              Touch
            </h2>
          </div>
          <div className="mt-12 space-y-3">
            <p className="font-sans text-sm text-primary-foreground/80">
              We reply within 24 hours.
            </p>
            <p className="font-sans text-sm text-primary-foreground/80">
              Free trial available on request.
            </p>
          </div>
        </div>

        {/* Right form */}
        <div className="md:col-span-3 p-10">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Name */}
            <div>
              <label className="font-heading text-xs font-extrabold uppercase tracking-widest text-muted-foreground block mb-2">
                Full Name
              </label>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type="text"
                    placeholder="John Doe"
                    className="w-full bg-background border-0 border-b-2 border-border px-0 py-3 font-sans text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-b-primary transition-colors duration-200"
                    style={{
                      borderBottomColor: errors.name
                        ? "var(--accent)"
                        : undefined,
                    }}
                  />
                )}
              />
              {errors.name && (
                <p className="font-sans text-xs text-accent mt-1">
                  {errors.name.message}
                </p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="font-heading text-xs font-extrabold uppercase tracking-widest text-muted-foreground block mb-2">
                Email Address
              </label>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type="email"
                    placeholder="email@example.com"
                    className="w-full bg-background border-0 border-b-2 border-border px-0 py-3 font-sans text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-b-primary transition-colors duration-200"
                    style={{
                      borderBottomColor: errors.email
                        ? "var(--accent)"
                        : undefined,
                    }}
                  />
                )}
              />
              {errors.email && (
                <p className="font-sans text-xs text-accent mt-1">
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Message */}
            <div>
              <label className="font-heading text-xs font-extrabold uppercase tracking-widest text-muted-foreground block mb-2">
                Message
              </label>
              <Controller
                name="message"
                control={control}
                render={({ field }) => (
                  <textarea
                    {...field}
                    rows={5}
                    placeholder="Tell us what you need..."
                    className="w-full bg-background border-0 border-b-2 border-border px-0 py-3 font-sans text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-b-primary transition-colors duration-200 resize-none"
                    style={{
                      borderBottomColor: errors.message
                        ? "var(--accent)"
                        : undefined,
                    }}
                  />
                )}
              />
              {errors.message && (
                <p className="font-sans text-xs text-accent mt-1">
                  {errors.message.message}
                </p>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-primary text-primary-foreground font-heading font-extrabold text-xs uppercase tracking-widest py-4 hover:bg-foreground hover:text-background transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed mt-2"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <svg
                    className="animate-spin h-4 w-4"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Sending...
                </span>
              ) : (
                "Send Message →"
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
